// CASH1-S2-U1 (docs/build-briefs/CASH1-S2-U1-pay-screens.md; PRD P1.1; DEC-399; design/mockups/cash1/cash1-1b, 2 and 3): the
// Cash screen at Pay and the Change due screen after it. One scrim-backed sheet built at runtime (check:chrome-ratchet refuses a
// new overlay written into index.html).
//
// FLOOR surface, touch only. Money is never typed: the amount handed over is Exact, one of the quick-cash buttons, or counted
// with the shared bills and coins steppers under Other. Nothing is sent until the Take button is tapped, so a stray tap on a
// quick amount moves no money. No tip on cash (owner ruling 2026-10-13, later). All text via textContent.
//
// The payment itself is sent by main.ts through the ordinary pay route (the idempotency key, the in-flight disabling and the
// refusal sentences are the card path's own). This module only draws and hands the chosen tender back.

import { renderDenominationGrid } from "./cashDenominationGrid.ts";
import { dollars, emptyCounts, totalCents, type DrawerCounts } from "./cashDrawerModel.ts";
import {
  changeFor,
  changeHeadline,
  dollarsOf,
  quickCashAmounts,
  takeCashLabel,
  tenderFor,
  type CashChoice,
} from "./cashTenderModel.ts";

export interface CashTenderPick {
  tenderedCents: number;
  denominations?: Record<string, number>;
}

export interface CashTenderController {
  /** The Cash screen for an amount due. `onTake` gets the tender when the Take button is tapped; the sheet is then closed. */
  openCash: (dueCents: number, onTake: (pick: CashTenderPick) => void) => void;
  /** The Change due screen, from the server's recorded figures. */
  showChange: (input: { changeCents: number; tenderedCents: number; dueCents: number }) => void;
  isOpen: () => boolean;
  /** Sign-out, or the sheet it was drawn for went away: close and forget everything. */
  reset: () => void;
}

const SCRIM_ID = "cash-tender-scrim";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  if (testId !== undefined) node.dataset["testid"] = testId;
  return node;
}

function button(
  className: string,
  text: string,
  testId: string,
  onClick: () => void,
): HTMLButtonElement {
  const node = el("button", className, text, testId);
  node.type = "button";
  node.addEventListener("click", onClick);
  return node;
}

export function createCashTender(): CashTenderController {
  let scrim: HTMLElement | undefined;
  let dueCents = 0;
  let choice: CashChoice = { kind: "exact" };
  let counts: DrawerCounts = emptyCounts();
  let onTake: ((pick: CashTenderPick) => void) | undefined;

  function close(): void {
    scrim?.remove();
    scrim = undefined;
    onTake = undefined;
    dueCents = 0;
    choice = { kind: "exact" };
    counts = emptyCounts();
  }

  function ensureScrim(): HTMLElement {
    if (scrim !== undefined) return scrim;
    const root = el("div", "scrim scrim-top", undefined, SCRIM_ID);
    root.id = SCRIM_ID;
    root.hidden = true;
    root.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && onTake !== undefined) close();
    });
    document.body.append(root);
    scrim = root;
    return root;
  }

  function shell(title: string, wide: boolean, closable: boolean): {
    sheet: HTMLElement;
    body: HTMLElement;
  } {
    const sheet = el(
      "div",
      wide ? "sheet cash-sheet cash-sheet-wide" : "sheet cash-sheet",
      undefined,
      "cash-tender-sheet",
    );
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    sheet.setAttribute("aria-labelledby", "cash-tender-title");
    const header = el("div", "sheet-header");
    const row = el("div", "sheet-title-row");
    const titleEl = el("span", "sheet-title", title, "cash-tender-title");
    titleEl.id = "cash-tender-title";
    row.append(titleEl);
    if (closable) {
      const x = button("btn-icon", "✕", "cash-tender-close-x", () => close());
      x.setAttribute("aria-label", "Cancel, do not take cash");
      row.append(x);
    }
    header.append(row);
    sheet.append(header);
    const body = el("div", "cash-body");
    sheet.append(body);
    return { sheet, body };
  }

  function isSelected(candidate: CashChoice): boolean {
    return choice.kind === candidate.kind && choice.cents === candidate.cents;
  }

  function optionButton(
    candidate: CashChoice,
    label: string,
    sub: string | undefined,
    testId: string,
    extraClass?: string,
  ): HTMLButtonElement {
    const selected = isSelected(candidate);
    const node = button(
      `cash-tender-opt${selected ? " selected" : ""}${extraClass === undefined ? "" : ` ${extraClass}`}`,
      label,
      testId,
      () => {
        choice = candidate;
        if (candidate.kind !== "other") counts = emptyCounts();
        renderCash();
      },
    );
    node.setAttribute("aria-pressed", selected ? "true" : "false");
    if (sub !== undefined) node.append(el("span", "cash-tender-opt-sub", sub));
    return node;
  }

  function renderCash(): void {
    const root = ensureScrim();
    root.replaceChildren();
    const { sheet, body } = shell("Cash", choice.kind === "other", true);
    const due = el("div", "cash-tender-due");
    due.append(
      el("div", "cash-tender-due-label", "Total due"),
      el("div", "cash-tender-due-value", dollarsOf(dueCents), "cash-tender-due"),
    );
    body.append(due);

    const options = el("div", "cash-tender-options", undefined, "cash-tender-options");
    options.append(
      optionButton(
        { kind: "exact" },
        "Exact",
        dollarsOf(dueCents),
        "cash-tender-exact",
        "cash-tender-opt-exact",
      ),
    );
    for (const cents of quickCashAmounts(dueCents)) {
      options.append(
        optionButton(
          { kind: "quick", cents },
          dollars(cents),
          undefined,
          `cash-tender-quick-${cents}`,
        ),
      );
    }
    options.append(
      optionButton({ kind: "other" }, "Other", undefined, "cash-tender-other", "cash-tender-opt-other"),
    );
    body.append(options);

    if (choice.kind === "other") {
      const sum = el("div", "cash-sum");
      const tile = el("div", "big");
      tile.append(el("div", "l", "Cash given"), el("div", "v", dollars(totalCents(counts)), "cash-tender-counted"));
      sum.append(tile);
      body.append(
        sum,
        renderDenominationGrid({
          counts,
          busy: false,
          testPrefix: "cash-tender",
          onChange: (next) => {
            counts = next;
            renderCash();
          },
        }),
      );
    }

    const pick = tenderFor(dueCents, choice, counts);
    const foot = el("div", "sheet-footer cash-sheet-foot");
    const cancel = button("btn-secondary", "Cancel", "cash-tender-cancel", () => close());
    const take = button(
      "btn-pay",
      pick === null ? "Take cash" : takeCashLabel(dueCents, pick.tenderedCents),
      "cash-tender-take",
      () => {
        const chosen = tenderFor(dueCents, choice, counts);
        const handler = onTake;
        if (chosen === null || handler === undefined) return;
        // Closed before the call goes out: a second tap has nothing to land on.
        close();
        handler(chosen);
      },
    );
    take.disabled = pick === null;
    if (pick !== null) {
      const change = changeFor(dueCents, pick.tenderedCents);
      if (change !== null && change > 0) {
        body.append(
          el("div", "cash-tender-change-preview", `Change ${dollarsOf(change)}`, "cash-tender-change-preview"),
        );
      }
    }
    foot.append(cancel, take);
    // The footer sits outside the scrolling body, so Take stays on screen while the bills and coins are counted.
    body.classList.add("cash-body-scroll");
    sheet.append(foot);
    root.append(sheet);
    root.hidden = false;
    take.focus();
  }

  function openCash(due: number, handler: (pick: CashTenderPick) => void): void {
    close();
    dueCents = due;
    onTake = handler;
    choice = { kind: "exact" };
    counts = emptyCounts();
    renderCash();
  }

  function showChange(input: { changeCents: number; tenderedCents: number; dueCents: number }): void {
    close();
    const root = ensureScrim();
    root.replaceChildren();
    const { sheet, body } = shell("Change due", false, false);
    const result = el("div", "cash-result");
    result.append(
      el(
        "div",
        input.changeCents > 0
          ? "cash-result-value cash-tender-change-value"
          : "cash-result-value cash-tender-change-value cash-tender-nochange",
        changeHeadline(input.changeCents),
        "cash-tender-change",
      ),
    );
    body.append(result);
    const pairs = el("div", "cash-pairs cash-pairs-two");
    const given = el("div");
    given.append(el("div", "l", "Cash given"), el("div", "v", dollarsOf(input.tenderedCents), "cash-tender-given"));
    const due = el("div");
    due.append(el("div", "l", "Total due"), el("div", "v", dollarsOf(input.dueCents), "cash-tender-given-due"));
    pairs.append(given, due);
    body.append(pairs);
    const foot = el("div", "sheet-footer");
    const done = button("btn-primary btn-full", "Done", "cash-tender-done", () => close());
    foot.append(done);
    body.append(foot);
    root.append(sheet);
    root.hidden = false;
    done.focus();
  }

  return {
    openCash,
    showChange,
    isOpen: () => scrim !== undefined && !scrim.hidden,
    reset: close,
  };
}
